import { useMemo, type ReactNode, type Ref } from "react";

// Every account keeps its own profile of thin lines, seeded by its id so it
// looks the same on any device and in either language. The lines are tallest
// around the avatar and thin out toward both edges.
const lineCount = 72;

function voiceprintPath(id: string) {
  let seed = 2166136261;
  for (const char of id) seed = Math.imul(seed ^ char.charCodeAt(0), 16777619);
  // Mulberry32: small, and evenly spread even for ids that differ by a letter.
  function random() {
    seed = (seed + 0x6d2b79f5) | 0;
    let mixed = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    mixed = (mixed + Math.imul(mixed ^ (mixed >>> 7), 61 | mixed)) ^ mixed;
    return ((mixed ^ (mixed >>> 14)) >>> 0) / 2 ** 32;
  }
  const swells = 5 + random() * 6;
  const phase = random() * 2 * Math.PI;
  let path = "";
  for (let line = 0; line < lineCount; line++) {
    const position = (line + 0.5) / lineCount;
    const envelope = Math.sin(Math.PI * position) ** 0.7;
    const voice = 0.7 + 0.3 * Math.sin(swells * Math.PI * position + phase);
    const height = 48 * envelope * voice * (0.45 + 0.55 * random());
    path += `M${(position * 1000).toFixed(1)} ${(-height).toFixed(1)}V${height.toFixed(1)}`;
  }
  return path;
}

// Live, the lines follow a --level set from outside.
export function Voiceprint({
  id,
  live = false,
  ref,
  children,
}: {
  id: string;
  live?: boolean;
  ref?: Ref<HTMLDivElement>;
  children: ReactNode;
}) {
  const path = useMemo(() => voiceprintPath(id), [id]);
  return (
    <div ref={ref} className="voiceprint" data-live={live || undefined}>
      <div className="voiceprint-lines" aria-hidden="true">
        <svg viewBox="0 -50 1000 100" preserveAspectRatio="none" fill="none">
          <path d={path} />
        </svg>
      </div>
      {children}
    </div>
  );
}
